import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { updateGroupDetails } from "../../../redux/group/groupSlice";
import { DailogBox } from "../../../components/Elements/DailogBox/DailogBox";
import { Input, Select } from "antd";
import { ReactComponent as BackIcon } from "../../../assets/svgs/back-button.svg";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";

const { TextArea } = Input;

const EditDetailForm = ({ onOpen, onClose, userData, onSave }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    groupName: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    description: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [popupData, setPopupData] = useState(null);

  useEffect(() => {
    if (userData) {
      setFormData({
        groupName: userData.groupName || "",
        address_1: userData.address_1 || "",
        address_2: userData.address_2 || "",
        city: userData.city || "",
        state: userData.state || "",
        pincode: userData.pincode || "",
        country: userData.country || "",
        description: userData.description || "",
      });
    }
  }, [userData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    let errors = {};
    if (!formData.groupName) errors.groupName = "Group Name is required";
    if (!formData.address_1) errors.address_1 = "Address 1 is required";
    if (!formData.city) errors.city = "City is required";
    if (!formData.state) errors.state = "State is required";
    if (!formData.pincode) errors.pincode = "Pincode is required";
    if (!formData.country) errors.country = "Country is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // SUCCESS POPUP LOGIC
  const handleSaveClick = async () => {
    if (!validateForm()) {
      return;
    }

    if (!userData?._id) {
      console.error("No group ID found");
      return;
    }

    setLoading(true);

    try {
      const resultAction = await dispatch(updateGroupDetails({
        id: userData._id,
        data: formData
      }));

      if (updateGroupDetails.fulfilled.match(resultAction)) {
        setPopupData({
          type: "success",
          title: "Success",
          message: "Group Edited Successfully",
        });

        if (onSave) onSave(); // Call parent onSave if provided to refresh data or manage its own popup state.

        setTimeout(() => {
          setPopupData(null);
          onClose();
        }, 2000);
      } else {
        setPopupData({
          type: "error",
          title: "Error",
          message: resultAction.payload || "Failed to update group",
        });
        setTimeout(() => {
          setPopupData(null);
        }, 3000);
      }
    } catch (err) {
      console.error("Failed to update group", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DailogBox open={onOpen} onCancel={onClose} width="900px" footer={null}>
      <div className="px-10 py-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-[#0B5E4B]">Edit Group</h2>
          {/* <div
            onClick={onClose}
            className="w-10 h-10 rounded-full border-2 border-[#0B5E4B] flex items-center justify-center cursor-pointer hover:bg-gray-100"
          >
            <BackIcon className="w-5 h-5 text-[#0B5E4B]" />
          </div> */}
        </div>

        <hr className="my-6" />

        {/* Group Name - Underlined Style as per image */}
        <div className="bg-[#F5F7F6] rounded-xl p-2 border-b border-gray-300">
          <Input
            name="groupName"
            value={formData.groupName}
            onChange={handleChange}
            variant="borderless"
            className="h-12 text-[15px]"
          />
        </div>
        {formErrors.groupName && <p className="text-red-500 text-xs mt-1 ml-2">{formErrors.groupName}</p>}

        {/* Address Grid */}
        <div className="grid grid-cols-2 gap-x-16 gap-y-10 mt-10">
          {[
            { name: "address_1", placeholder: "Address 1" },
            { name: "address_2", placeholder: "Address 2" },
            { name: "city", placeholder: "City" },
            { name: "state", placeholder: "State" },
            { name: "pincode", placeholder: "Pincode" },
            { name: "country", placeholder: "Country" },
          ].map((field) => (
            <div key={field.name}>
              <div className="border-b border-gray-300 focus-within:border-[#0B5E4B] transition-all">
                <Input
                  name={field.name}
                  variant="borderless"
                  value={formData[field.name]}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  className="px-0 pb-1"
                />
              </div>
              {formErrors[field.name] && <p className="text-red-500 text-xs mt-1">{formErrors[field.name]}</p>}
            </div>
          ))}
        </div>

        {/* Description */}
        <div className="mt-10">
          <p className="text-[#0B5E4B] text-sm mb-2 font-medium">Description</p>
          <TextArea
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData((p) => ({ ...p, description: e.target.value }))
            }
            className="rounded-xl bg-[#F5F7F6] px-4 py-3 border-none resize-none"
          />
        </div>

        <hr className="my-8" />

        {/* Action Buttons */}
        <div className="flex justify-center gap-8">
          <button
            onClick={onClose}
            className="px-10 h-12 rounded-xl border border-[#0B5E4B] text-[#0B5E4B] bg-[#E6F0ED] font-semibold hover:bg-[#d8e7e2] transition-all"
          >
            Cancel
          </button>

          <button
            onClick={handleSaveClick}
            disabled={loading}
            className="px-12 h-12 rounded-xl bg-[#0B5E4B] text-white font-semibold shadow hover:bg-[#084a3b] transition-all"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Success Popup Card */}
      {popupData && (
        <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center">
          <PopupCard
            type={popupData.type}
            title={popupData.title}
            message={popupData.message}
          />
        </div>
      )}
    </DailogBox>
  );
};

export default EditDetailForm;